// Google Maps API configuration
export const GOOGLE_MAPS_CONFIG = {
  // Google Maps API Key - should be set in environment variables
  apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "",

  // Default options for Places Autocomplete
  autocompleteOptions: {
    types: ["address"],
    componentRestrictions: { country: "us" }, // Restrict to US addresses
    fields: ["address_components", "formatted_address", "geometry", "place_id"],
  },

  // Address component types mapping
  addressComponentTypes: {
    street_number: "street_number",
    route: "route",
    locality: "city",
    administrative_area_level_1: "state",
    postal_code: "zip_code",
    country: "country",
  },
};

// Utility function to extract address components from Google Places result
export const extractAddressComponents = (
  place: google.maps.places.PlaceResult
) => {
  const components: Record<string, string> = {};

  if (place.address_components) {
    place.address_components.forEach((component) => {
      const types = component.types;

      if (types.includes("street_number")) {
        components.street_number = component.long_name;
      } else if (types.includes("route")) {
        components.route = component.long_name;
      } else if (types.includes("locality")) {
        components.city = component.long_name;
      } else if (types.includes("administrative_area_level_1")) {
        components.state = component.short_name;
      } else if (types.includes("postal_code")) {
        components.zip_code = component.long_name;
      } else if (types.includes("country")) {
        components.country = component.long_name;
      }
    });
  }

  // Combine street number and route for full address
  const streetNumber = components.street_number || "";
  const route = components.route || "";
  const fullAddress = `${streetNumber} ${route}`.trim();

  return {
    line1: fullAddress,
    city: components.city || "",
    state: components.state || "",
    zipCode: components.zip_code || "",
  };
};

// Check if Google Maps API is loaded
export const isGoogleMapsLoaded = (): boolean => {
  return (
    typeof window !== "undefined" &&
    typeof window.google !== "undefined" &&
    typeof window.google.maps !== "undefined" &&
    typeof window.google.maps.places !== "undefined"
  );
};

// Load Google Maps API script
export const loadGoogleMapsAPI = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (isGoogleMapsLoaded()) {
      resolve();
      return;
    }

    if (!GOOGLE_MAPS_CONFIG.apiKey) {
      reject(new Error("Google Maps API key is not configured"));
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_CONFIG.apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      console.log("Google Maps API loaded successfully");
      resolve();
    };

    script.onerror = () => {
      reject(new Error("Failed to load Google Maps API"));
    };

    document.head.appendChild(script);
  });
};
